/**
 * TenantRedirect Component
 *
 * Decides where a user with no tenant in the URL belongs: the organization they
 * had selected, their first one, or the purchase funnel (resume or pricing).
 *
 * Every branch is taken from the tenant registry — what the regional APIs
 * answered — never from the token claim and never from an absence that could not
 * be confirmed. Concluding "you have no organizations" from a failed request
 * would push an existing user into creating a duplicate, and concluding it from a
 * stale claim is what hid a newly created organization (ESCLOUD-268).
 *
 * When absence genuinely cannot be established, this says so instead of
 * rendering forever: an unreachable region is a state a user has to be able to
 * see and retry.
 *
 * It does not look at the email address: verification is an in-app banner and a
 * condition of the clinical gate, never a routing question (ESCLOUD-333).
 */

import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import { RetryPanel } from '@/components/feedback/retry-panel';
import { RouteDecisionSkeleton } from '@/components/feedback/route-decision-skeleton';
import { Button } from '@/components/ui/button';
import { useAuth0, useRegion, useTenant } from '@/context';
import { useTenantLookup, useTenantsOverview } from '@/context/tenant-registry-context';
import { userKeys } from '@/hooks/use-current-user';
import { isProvenNoOpenIntent, useCurrentPurchaseIntent } from '@/hooks/use-purchase-intent';
import { regionKeys } from '@/hooks/use-regions';
import { errorIds, logError } from '@/lib/log-error';
import { PRICING_ROUTE, resumeRouteForIntent } from '@/lib/onboarding-funnel';
import {
    clearPurchaseIntentHint,
    readPurchaseIntentHint,
    writePurchaseIntentHint,
} from '@/lib/purchase-intent-hint';
import { DEFAULT_REGION } from '@/lib/region-config';

export function TenantRedirect() {
    const { tenantId, clearTenant } = useTenant();
    const { entries, status } = useTenantsOverview();
    const selected = useTenantLookup(tenantId);

    // Only a confirmed absence drops the selection.
    const selectionAbsent = selected.status === 'absent';
    useEffect(() => {
        if (selectionAbsent) {
            clearTenant();
        }
    }, [clearTenant, selectionAbsent]);

    // An abandoned purchase resumes where it stopped. Only the hinted region is
    // asked, never every region, and only once the registry has proven there is
    // no workspace to open instead. The intent is server-side state, so a
    // browser without the hint (a private window, another device) asks the
    // default region rather than concluding there is nothing to resume.
    const hint = useMemo(() => readPurchaseIntentHint(), []);
    const intent = useCurrentPurchaseIntent(hint?.region ?? DEFAULT_REGION, { enabled: status === 'none' });

    // The funnel pages find the intent through the hint alone: re-seed it
    // from the server's answer before sending the user there.
    const found = intent.data;
    useEffect(() => {
        if (found && !hint) {
            writePurchaseIntentHint({ region: found.region, intentId: found.id, planId: found.planId });
        }
    }, [found, hint]);

    // Only a proven "no open intent" (a 404) forgets the hint; any other
    // failure is not evidence of anything.
    const hintProvenGone = !!hint && !!intent.error && isProvenNoOpenIntent(intent.error);
    useEffect(() => {
        if (hintProvenGone) {
            clearPurchaseIntentHint();
        }
    }, [hintProvenGone]);

    const unprovable = status === 'unprovable' || selected.status === 'unprovable';

    if (status === 'pending' || selected.status === 'pending') {
        return <RouteDecisionSkeleton />;
    }

    if (unprovable) {
        return <TenantsUnavailable />;
    }

    if (selected.status === 'confirmed') {
        return <Navigate to={`/tenants/${selected.entry.tenant.id}`} replace />;
    }

    if (entries.length > 0) {
        return <Navigate to={`/tenants/${entries[0].tenant.id}`} replace />;
    }

    // Every deployed region answered, and none of them has a workspace for
    // this user. An open purchase intent resumes the funnel; otherwise pricing
    // is where a purchase starts (funnel v2).
    if (intent.isPending) return <RouteDecisionSkeleton />;
    if (intent.data) return <Navigate to={resumeRouteForIntent(intent.data)} replace />;
    // Any other failure, proven gone or not, is not evidence of a workspace to
    // open: pricing is the safe default. A proven 404 also forgets the hint
    // (effect above).
    return <Navigate to={PRICING_ROUTE} replace />;
}

/**
 * Shown when the organization list could not be established. Deliberately not a
 * redirect to organization creation: an existing user must not be nudged into
 * creating a duplicate because a request failed.
 *
 * The retry has to cover every reason absence could not be established, and they
 * do not all live in the same place: the regional queries and the deployed-region
 * list are React Query caches, but a claims failure is provider state that only a
 * fresh token attempt clears. Invalidating alone would leave a user whose session
 * failed stuck here until a full page reload.
 */
function TenantsUnavailable() {
    const queryClient = useQueryClient();
    const { claimsFailed, refreshToken } = useRegion();
    const { loginWithRedirect } = useAuth0();
    const [retrying, setRetrying] = useState(false);

    useEffect(() => {
        logError(
            errorIds.TENANT_LIST_UNAVAILABLE,
            'Could not establish the list of organizations — a region, the region list ' +
                'or the token claims did not answer.',
            { claimsFailed }
        );
    }, [claimsFailed]);

    const retry = async () => {
        setRetrying(true);
        try {
            // Only when the claims are what failed: otherwise this is a pointless
            // token exchange. refreshToken() rethrows, and a retry that fails
            // again must leave the user on this screen, not unhandled.
            if (claimsFailed) {
                await refreshToken().catch(() => undefined);
            }
            await Promise.all([
                queryClient.invalidateQueries({ queryKey: userKeys.tenants() }),
                queryClient.invalidateQueries({ queryKey: regionKeys.all }),
            ]);
        } finally {
            setRetrying(false);
        }
    };

    return (
        <RetryPanel
            title="Your organizations could not be loaded"
            description={
                claimsFailed
                    ? 'Your session could not be verified, so we have not shown you a possibly incomplete list. Your data is unaffected.'
                    : 'One of our regions did not respond, so we have not shown you a possibly incomplete list. Your data is unaffected.'
            }
            actions={
                <>
                    <Button onClick={retry} disabled={retrying}>
                        {retrying ? 'Trying…' : 'Try again'}
                    </Button>
                    {/* A revoked or expired session cannot be retried into
                        working — signing in again is the only real recovery. */}
                    {claimsFailed && (
                        <Button
                            variant="outline"
                            onClick={() =>
                                loginWithRedirect({ appState: { returnTo: window.location.pathname } })
                            }
                        >
                            Sign in again
                        </Button>
                    )}
                </>
            }
        />
    );
}
